"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import type {
  AccommodationFacilityType,
  ArchitectureTypeSeries,
  ArchitectureTypeSize,
} from "../../../../../__generated__/gql/graphql";
import { useGtm } from "../../../../../lib/google-tag-manager/gtm";

import {
  useAccommodationFilter,
  useAreaFilter,
  facilityFilterItems,
  architectureFilterItems,
} from "./_hooks/accommodation-filter";
import { useFilterModal } from "./filter-modal";

export type MatchesAccommodationFilter = (accommodation: {
  facilityTypes: AccommodationFacilityType[];
  architectureType: {
    series: ArchitectureTypeSeries;
    seriesShortName: string;
    size?: ArchitectureTypeSize | null;
  };
}) => boolean;

export type AreaItem = { slug: string; name: string };

export type ActiveFilter = {
  type: "area" | "facility" | "architecture";
  value: string;
  label: string;
  onRemove: () => void;
};

type AccommodationFilterContextValue = {
  hasAccommodationFilter: boolean;
  hasAnyActiveFilter: boolean;
  areaFilter: ReadonlySet<string>;
  matchesAccommodationFilter: MatchesAccommodationFilter;
  openFilterModal: () => void;
  activeFilters: ActiveFilter[];
  addAreaFilter: (slug: string) => void;
  addFacilityFilter: (type: AccommodationFacilityType) => void;
};

const AccommodationFilterContext =
  createContext<AccommodationFilterContextValue | null>(null);

export function useAccommodationFilterContext(): AccommodationFilterContextValue {
  const ctx = useContext(AccommodationFilterContext);
  if (!ctx) {
    throw new Error(
      "useAccommodationFilterContext must be used within AccommodationFilterProvider",
    );
  }
  return ctx;
}

// Providerの中にフィルターモーダルのopen状態を閉じ込め、
// FilterBarやCalendarEmptyStateがContext経由で直接アクセスできるようにする。
// CICODateSelectionModalProviderと同じパターン。
export function AccommodationFilterProvider({
  areaItems,
  children,
}: {
  areaItems: AreaItem[];
  children: ReactNode;
}) {
  const { areaFilter, setAreaFilter } = useAreaFilter();
  const {
    facilityFilter,
    setFacilityFilter,
    architectureFilter,
    setArchitectureFilter,
    hasAccommodationFilter,
    matchesAccommodationFilter,
  } = useAccommodationFilter();

  const hasAnyActiveFilter = hasAccommodationFilter || areaFilter.size > 0;

  const { openModal: openFilterModalRaw, renderModal: renderFilterModal } =
    useFilterModal({
      areaItems,
      areaFilter,
      setAreaFilter,
      facilityFilter,
      setFacilityFilter,
      architectureFilter,
      setArchitectureFilter,
    });

  const gtm = useGtm();
  const openFilterModal = useCallback(() => {
    gtm.sendEvent("rsv_cal_filter_opened");
    openFilterModalRaw();
  }, [gtm, openFilterModalRaw]);

  // アクティブなフィルター条件を収集
  const activeFilters = useMemo((): ActiveFilter[] => {
    const areaFilters: ActiveFilter[] = [...areaFilter]
      .map((slug) => {
        const area = areaItems.find((a) => a.slug === slug);
        if (!area) return null;
        return {
          type: "area" as const,
          value: slug,
          label: area.name,
          onRemove: () => setAreaFilter(removeFromSet(areaFilter, slug)),
        };
      })
      .filter((f) => f != null);

    const facilityFilters: ActiveFilter[] = [...facilityFilter]
      .map((type) => {
        const item = facilityFilterItems.find((f) => f.type === type);
        if (!item) return null;
        return {
          type: "facility" as const,
          value: type,
          label: item.label,
          onRemove: () =>
            setFacilityFilter(removeFromSet(facilityFilter, type)),
        };
      })
      .filter((f) => f != null);

    const archFilters: ActiveFilter[] = [...architectureFilter].map((value) => {
      const item = architectureFilterItems.find((a) => a.value === value);
      return {
        type: "architecture" as const,
        value,
        label: item?.label ?? value,
        onRemove: () =>
          setArchitectureFilter(removeFromSet(architectureFilter, value)),
      };
    });

    return [...areaFilters, ...facilityFilters, ...archFilters];
  }, [
    areaFilter,
    areaItems,
    facilityFilter,
    architectureFilter,
    setAreaFilter,
    setFacilityFilter,
    setArchitectureFilter,
  ]);

  const addAreaFilter = useCallback(
    (slug: string) => {
      const next = new Set(areaFilter);
      next.add(slug);
      setAreaFilter(next);
    },
    [areaFilter, setAreaFilter],
  );

  const addFacilityFilter = useCallback(
    (type: AccommodationFacilityType) => {
      const next = new Set(facilityFilter);
      next.add(type);
      setFacilityFilter(next);
    },
    [facilityFilter, setFacilityFilter],
  );

  const value = useMemo(
    (): AccommodationFilterContextValue => ({
      hasAccommodationFilter,
      hasAnyActiveFilter,
      areaFilter,
      matchesAccommodationFilter,
      openFilterModal,
      activeFilters,
      addAreaFilter,
      addFacilityFilter,
    }),
    [
      hasAccommodationFilter,
      hasAnyActiveFilter,
      areaFilter,
      matchesAccommodationFilter,
      openFilterModal,
      activeFilters,
      addAreaFilter,
      addFacilityFilter,
    ],
  );

  return (
    <AccommodationFilterContext.Provider value={value}>
      {children}
      {renderFilterModal()}
    </AccommodationFilterContext.Provider>
  );
}

function removeFromSet<T>(set: ReadonlySet<T>, item: T): Set<T> {
  const next = new Set(set);
  next.delete(item);
  return next;
}
